import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import {autoinject, BindingEngine, observable, transient} from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { DataType, SourceType } from "../../../../models/queries/queries.models";
import { MdmPipelineClientService } from "../../../../services/modules/mdm-pipeline.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import * as moment from "moment";
import * as Enumerable from "linq";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { MdmPipelineModel } from "../../../../models/mdm-pipelines/mdm-pipelines.models";

@autoinject()
@transient()
export class Pipelines extends CustomGridPageBase {

    mdmPipelines: MdmPipelineModel[];
    constructor(i18N: I18N, protected bindingEngine: BindingEngine,
        private mdmPipelineClientService: MdmPipelineClientService,
        private notificationClientService: NotificationClientService,
        private dialogClientService: DialogClientService) {

        super(i18N, 'Lists', bindingEngine);
        this.loadPipelines();
    }
    
    @observable()
    public searchFilter: string = null;

    private reloadTimeout;

    debounceReload(immediate: boolean = false, ...args)
    {
        const func = this.reloadWithSearchFilter;
        const wait = 100;

        const later = function () {
            this.reloadTimeout = null;
            if (!immediate) func.apply(this, args);
        };

        clearTimeout(this.reloadTimeout);

        this.reloadTimeout = setTimeout(later, wait);

        if (immediate) func.apply(this, args);
    }

    searchFilterChanged() {
        this.debounceReload(false);
    }

    clearSearchFilter() {
        this.searchFilter = null;
    }

    searchKeyDown($event) {
        if ($event && $event.detail) {
            const code = $event.detail.keyCode;
            if (code === 13) {
                this.reloadWithSearchFilter()
            }
        }
    }

    public loadPipelines() {
        this.mdmPipelineClientService.getMdmPipelines().then(response => {
            const columns = [];
            columns.push(
                {
                    sortOrder: 0,
                    columnOrder: 0,
                    dataType: DataType.Default,
                    isSortable: true,
                    name: 'name',
                    title: 'Name'
                },
                {
                    sortOrder: 0,
                    columnOrder: 0,
                    dataType: DataType.Default,
                    isSortable: true,
                    name: 'cron',
                    title: 'Cron'
                },
                {
                    sortOrder: 0,
                    columnOrder: 0,
                    dataType: DataType.Default,
                    isSortable: true,
                    name: 'timezone',
                    title: 'Timezone'
                },
                {
                    sortOrder: 0,
                    columnOrder: 0,
                    dataType: DataType.DateTime,
                    isSortable: true,
                    name: 'nextExecutionDate',
                    title: 'Next Execution Date',
                    valueConverter: (value) => value ? moment(value).format("DD MMM YYYY HH:mm:ss") : null
                },
                {
                    sortOrder: 0,
                    columnOrder: 0,
                    dataType: DataType.DateTime,
                    isSortable: true,
                    name: 'lastExecutionDate',
                    title: 'Last Execution Date',
                    valueConverter: (value) => value ? moment(value).format("DD MMM YYYY HH:mm:ss") : null
                },
                {
                    sortOrder: 0,
                    columnOrder: 0,
                    dataType: DataType.Default,
                    isSortable: true,
                    name: 'lastJobState',
                    title: 'Last Pipeline State'
                },
                {
                    columnOrder: 0,
                    sortOrder: 0,
                    dataType: DataType.CustomTemplate,
                    isSortable: true,
                    instancePerRow: true,
                    name: 'trigger',
                    title: 'Trigger',
                    isAscending: true,
                    value: '<a class="link" click.trigger="runPipeline(item)">Trigger</a>',
                    parameter: {
                        item: null,
                        runPipeline: (item) => {
                            this.mdmPipelineClientService.runPipeline(item.name).then(() => {
                                this.notificationClientService.show("Pipeline Triggered", "", NotificationType.Success);
                            }).catch(() => {
                                this.notificationClientService.show("Failed to run Pipeline", "", NotificationType.Error);
                            });
                        }
                    }
                });

            columns.forEach((column, index) => {
                column.columnOrder = index;
                column.sortOrder = index;
            });

            this.columns = columns;
            this.itemSource = {
                sourceType: SourceType.Collection,
                source: response.mdmPipelines,
                columns: this.columns,
                initialFilters: []
            };
            this.mdmPipelines = response.mdmPipelines;
        });
    }

    public reloadWithSearchFilter() {
        if(this.searchFilter) {
            const collection = Enumerable.from(this.mdmPipelines).where(x=> x.name.toLowerCase().includes(this.searchFilter)).toArray();
            this.itemSource = {
                sourceType: SourceType.Collection,
                source: collection,
                columns: this.columns,
                initialFilters: []
            };
            this.dataGridVM.reloadData();
        }
        else {
            this.itemSource = {
                sourceType: SourceType.Collection,
                source: this.mdmPipelines,
                columns: this.columns,
                initialFilters: []
            };
            if(this.dataGridVM) {
                this.dataGridVM.reloadData();
            }
        }
    } 

    public customExecution() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CustomPipelineExecutionDialog, {}, true, true, false, 'right');
    }
}